import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Typography, Button } from '@mui/material';
import { FaPlus, FaRegTrashAlt } from 'react-icons/fa';
import { UserContext } from '../../../context/UserContext';
import API from '../../../config/axiosConfig';
import CommonLayout from '../../../components/CommonLayout';
import DropdownWithIcon from '../../../components/DropdownWithIcon';
import Loader from '../../../components/Loader';
import hospitalIcon from '../../../assets/protocols/hospital.svg';
import {
    defaultSettingsFor,
    normalizePhototherapySettings,
} from '../../../apps/phototherapy/phototherapyConfig';
import { protocolFileHref } from '../../../apps/phototherapy/protocolFiles';
import ConfigFilesEditor from '../../../components/ConfigFilesEditor';
import { deleteConfigFile } from '../../../services/configFilesApi';
import './PhototherapyConfig.scss';

const ArrowLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" />
    </svg>
);

const MG_DL = 'מ"ג/ד"ל';

// Numeric settings, grouped so each screenful is one clinical topic.
const NUMBER_GROUPS = [
    {
        title: 'טיפול באור',
        fields: [
            {
                key: 'subthresholdDeltaMgDl',
                label: 'טווח "מתקרב לסף"',
                unit: MG_DL,
                help: 'מתחת לסף טיפול באור. בטווח זה מוצגת אזהרה ואפשרות לשקול טיפול באשפוז.',
            },
            {
                key: 'stopPhototherapyDeltaMgDl',
                label: 'הפסקת טיפול - הפרש מתחת לסף',
                unit: MG_DL
            },
        ],
    },
    {
        title: 'הסלמת טיפול (Escalation of Care)',
        fields: [
            {
                key: 'escalationDeltaMgDl',
                label: 'הפרש מתחת לסף החלפת דם',
                unit: MG_DL,
                help: 'ערך בילירובין בטווח זה מתחת לסף החלפת הדם מוגדר כהסלמת טיפול, ומציג את שלבי ההסלמה. לפי AAP 2022 ‏- 2 מ"ג/ד"ל.',
                positive: true,
            },
        ],
    },
    {
        title: 'מדידה מלעורית (TcB)',
        fields: [
            {
                key: 'tcbToTsbDeltaMgDl',
                label: 'אימות TSB - הפרש מסף הטיפול',
                unit: MG_DL,
                help: 'TcB קרוב מכך לסף מחייב מדידת TSB בדם.',
            },
            {
                key: 'tcbToTsbAbsoluteMgDl',
                label: 'אימות TSB - ערך מוחלט',
                unit: MG_DL,
                help: 'TcB בערך זה ומעלה מחייב מדידת TSB בדם. ',
            },
        ],
    },
    {
        title: 'בירור סוג דם ו-DAT',
        fields: [
            {
                key: 'bloodTypeDatDeltaMgDl',
                label: 'הפרש מסף הטיפול של ילודים בסיכון',
                unit: MG_DL,
                help: 'ערך בילירובין קרוב מכך לסף הטיפול בעקומה של ילודים עם גורמי סיכון לנוירוטוקסיות מחייב דגימה לסוג דם ו-DAT, גם אם ליילוד עצמו אין גורמי סיכון. השאירו ריק כדי לא להציג את ההנחיה.',
                nullable: true,
            },
        ],
    },
    {
        title: 'המלצות נוספות',
        fields: [
            {
                key: 'hearingTestTsbMgDl',
                label: 'בדיקת שמיעה (BERA) מערך TSB',
                unit: MG_DL,
                help: 'ההמלצה מוצגת גם בכל מקרה של הסלמת טיפול. השאירו ריק כדי לא להציג אותה כלל.',
                nullable: true,
            },
            {
                key: 'prolongedJaundiceFromHours',
                label: 'צהבת ממושכת - מגיל',
                unit: 'שעות',
                help: 'השאירו ריק כדי לא להציג את ההערה.',
                nullable: true,
            },
            {
                key: 'prolongedJaundiceDeltaMgDl',
                label: 'צהבת ממושכת - הפרש מהסף',
                unit: MG_DL,
                help: 'רלוונטי רק כאשר מולא גיל לצהבת ממושכת.',
            },
        ],
    },
];

const NUMBER_FIELDS = NUMBER_GROUPS.flatMap((g) => g.fields);

// Free-text fields, all edited as a textarea. `kind: 'text'` is one paragraph;
// anything else is a list, one item per line. `tab` says which tab renders it.
// They share one draft state and are saved together. Order here is the order on
// the page, and mirrors the order of the sections in the app.
const TEXTAREA_FIELDS = [
    {
        key: 'followUpNotes',
        tab: 'followup',
        label: 'הנחיות כלליות למעקב לאחר השחרור',
        help: 'מוצגות מתחת לטבלת המעקב במסך "מעקב". שורה לכל הנחיה.',
        rows: 4,
    },
    {
        key: 'neurotoxicityRiskFactorsNote',
        kind: 'text',
        label: 'הסבר - גורמי סיכון לנוירוטוקסיות',
        help: 'מוצג מתחת לכותרת הראשונה במסך "גורמי סיכון", ומסביר למה משמשים הגורמים האלה (בחירת עקומת הטיפול).',
        rows: 4,
    },
    {
        key: 'neurotoxicityRiskFactors',
        label: 'גורמי סיכון לנוירוטוקסיות',
        help: 'קובעים באיזו עקומה משתמשים - זו הרשימה שמאחורי תיבת הסימון במחשבון. שורה לכל גורם.',
        rows: 6,
    },
    {
        key: 'monitoringFrequency',
        label: 'תדירות המעקב',
        help: 'מוצגת בסעיף נפרד במסך "גורמי סיכון". אינה משנה אף סף טיפול. שורה לכל כלל. השאירו ריק כדי לא להציג את הסעיף.',
        rows: 6,
    },
    {
        key: 'phototherapyStartLabs',
        label: 'בדיקות מעבדה עם תחילת טיפול באור',
        help: 'שורה לכל בדיקה.',
        rows: 6,
    },
    {
        key: 'postPhototherapyNotes',
        label: 'מעקב במהלך טיפול באור ולאחריו',
        help: 'שורה לכל הנחיה.',
        rows: 6,
    },
    {
        key: 'escalationSteps',
        label: 'שלבי הסלמת טיפול',
        help: 'מוצגים כשערך הבילירובין מגיע לסף הסלמת הטיפול. שורה לכל שלב.',
        rows: 7,
    },
];

const FOLLOW_UP_TEXTAREA_FIELDS = TEXTAREA_FIELDS.filter((f) => f.tab === 'followup');
const TEXTS_TAB_FIELDS = TEXTAREA_FIELDS.filter((f) => f.tab !== 'followup');

const TABS = [
    { key: 'thresholds', label: 'ספים' },
    { key: 'followup', label: 'מעקב לאחר שחרור' },
    { key: 'texts', label: 'טקסטים והנחיות' },
    { key: 'files', label: 'פרוטוקולים' },
];

const toLines = (arr) => (arr || []).join('\n');
const fromLines = (text) => text.split('\n').map((s) => s.trim()).filter(Boolean);
const numberOrEmpty = (v) => (v === null || v === undefined ? '' : String(v));

/**
 * Admin console for a jaundice-calculator app. Every app in
 * `apps/phototherapy/variants.ts` shares this page; the `variant` prop is what
 * decides which AppConfig rows it reads and writes, so two apps configured from
 * the same screen never collide.
 */
const PhototherapyConfigPage = ({ variant }) => {
    const { appKey, configName } = variant;
    const defaults = useMemo(() => defaultSettingsFor(variant), [variant]);
    const { user, hospitals, permittedApps } = useContext(UserContext);
    const [selectedHospital, setSelectedHospital] = useState('');
    const [availableHospitals, setAvailableHospitals] = useState([]);
    const [hospitalsWithEditAccess, setHospitalsWithEditAccess] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('thresholds');
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    // Draft state. Numbers and lists stay strings while editing so the user can type freely.
    const [protocol, setProtocol] = useState(defaults.protocol);
    const [numbers, setNumbers] = useState({});
    const [lists, setLists] = useState({});
    const [bands, setBands] = useState([]);
    const [before12hText, setBefore12hText] = useState('');
    const [protocolFiles, setProtocolFiles] = useState([]);
    // Blob paths of files the user removed from the list. They are deleted only
    // once the configuration that stopped referencing them has been saved —
    // deleting on the click would leave the stored list pointing at a file that
    // no longer exists whenever the admin walks away without saving.
    const [pendingFileDeletions, setPendingFileDeletions] = useState([]);
    const [dirty, setDirty] = useState(false);
    // False until an AppConfig row with AAP values exists for this hospital. Until
    // then the AAP form shows the shipped defaults, which are not yet in effect.
    const [hasStoredAapConfig, setHasStoredAapConfig] = useState(false);

    const loadDraft = useCallback((settings) => {
        const a = settings.aap2022;
        setProtocol(settings.protocol);
        setNumbers(Object.fromEntries(NUMBER_FIELDS.map((f) => [f.key, numberOrEmpty(a[f.key])])));
        setLists(
            Object.fromEntries(
                TEXTAREA_FIELDS.map((f) => [f.key, f.kind === 'text' ? a[f.key] ?? '' : toLines(a[f.key])])
            )
        );
        setBands(
            a.followUpBands.map((b) => ({
                minDelta: numberOrEmpty(b.minDelta),
                maxDelta: numberOrEmpty(b.maxDelta),
                recommendation: b.recommendation,
            }))
        );
        setBefore12hText(a.followUpBefore12hText);
        setProtocolFiles(settings.protocolFiles);
        // Whatever was queued belongs to the draft being replaced. Those files
        // are still referenced by the configuration on screen now, so they must
        // not be deleted.
        setPendingFileDeletions([]);
        setDirty(false);
    }, []);

    useEffect(() => {
        if (hospitals && hospitals.length > 0 && user && permittedApps) {
            let editableHospitals = [];
            if (user.isSuperAdmin) {
                editableHospitals = [...hospitals];
            } else {
                const app = permittedApps.find((a) => a.key === appKey);
                if (app) {
                    const permittedHospitalIds = app.admins
                        .filter((admin) => admin.user._id === user._id)
                        .map((admin) => admin.hospital._id);
                    editableHospitals = hospitals.filter((h) => permittedHospitalIds.includes(h._id));
                }
            }
            setHospitalsWithEditAccess(editableHospitals);

            const hospitalOptions = [...hospitals];
            if (user.isSuperAdmin) {
                hospitalOptions.push({ _id: 'apps', nameHebrew: 'ברירת מחדל', nameEnglish: 'apps' });
            }
            setAvailableHospitals(hospitalOptions);

            if (editableHospitals.length === 1) {
                setSelectedHospital(editableHospitals[0].nameEnglish);
            } else if (user.isSuperAdmin) {
                setSelectedHospital('apps');
            } else if (editableHospitals.length > 0) {
                setSelectedHospital(editableHospitals[0].nameEnglish);
            } else if (hospitalOptions.length > 0) {
                setSelectedHospital(hospitalOptions[0].nameEnglish);
            }
        }
    }, [hospitals, user, permittedApps, appKey]);

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
        setTimeout(() => setNotification((prev) => ({ ...prev, open: false })), 3500);
    };

    const fetchConfig = useCallback(async (hospital) => {
        if (!hospital) return;
        try {
            setLoading(true);
            const response = await API.get(`/api/configs/${appKey}/${configName}/${hospital}`);
            const stored = response.data?.success ? response.data.data : null;
            setHasStoredAapConfig(Boolean(stored && typeof stored === 'object' && stored.aap2022));
            loadDraft(normalizePhototherapySettings(stored ?? {}, variant));
        } catch (error) {
            console.error('Error fetching phototherapy settings:', error);
            setHasStoredAapConfig(false);
            loadDraft(defaults);
            showNotification('שגיאה בטעינת ההגדרות - מוצגות ברירות המחדל', 'error');
        } finally {
            setLoading(false);
        }
    }, [loadDraft, appKey, configName, variant, defaults]);

    useEffect(() => {
        if (selectedHospital) fetchConfig(selectedHospital);
    }, [selectedHospital, fetchConfig]);

    const canEdit =
        user?.isSuperAdmin || hospitalsWithEditAccess.some((h) => h.nameEnglish === selectedHospital);

    const buildSettings = () => {
        const errors = [];
        const aap2022 = {};
        for (const f of NUMBER_FIELDS) {
            const raw = String(numbers[f.key] ?? '').trim();
            if (raw === '') {
                if (f.nullable) {
                    aap2022[f.key] = null;
                    continue;
                }
                errors.push(`יש למלא ערך עבור "${f.label}"`);
                continue;
            }
            const n = Number(raw.replace(',', '.'));
            // `positive` fields must not be zero: a zero escalation band would
            // silently drop the escalation-of-care protocol out of the app.
            if (!Number.isFinite(n) || n < 0 || (f.positive && n === 0)) {
                errors.push(`ערך לא תקין עבור "${f.label}"`);
                continue;
            }
            aap2022[f.key] = n;
        }
        for (const f of TEXTAREA_FIELDS) {
            const raw = lists[f.key] ?? '';
            aap2022[f.key] = f.kind === 'text' ? raw.trim() : fromLines(raw);
        }
        const parsedBands = [];
        bands.forEach((b, i) => {
            const min = Number(String(b.minDelta).replace(',', '.'));
            const maxRaw = String(b.maxDelta ?? '').trim();
            const max = maxRaw === '' ? null : Number(maxRaw.replace(',', '.'));
            if (!Number.isFinite(min) || (max !== null && !Number.isFinite(max))) {
                errors.push(`שורה ${i + 1} בטבלת המעקב: ערכי ההפרש חייבים להיות מספרים`);
                return;
            }
            if (max !== null && max < min) {
                errors.push(`שורה ${i + 1} בטבלת המעקב: הערך המרבי קטן מהמזערי`);
                return;
            }
            if (!b.recommendation.trim()) {
                errors.push(`שורה ${i + 1} בטבלת המעקב: חסרה המלצה`);
                return;
            }
            parsedBands.push({ minDelta: min, maxDelta: max, recommendation: b.recommendation.trim() });
        });
        if (parsedBands.length === 0) errors.push('טבלת המעקב חייבת לכלול לפחות שורה אחת');
        parsedBands.sort((a, b) => a.minDelta - b.minDelta);
        aap2022.followUpBands = parsedBands;
        aap2022.followUpBefore12hText = before12hText.trim();
        // A file with no title would render as an empty tile in the app, so it
        // falls back to its own file name rather than being saved blank.
        const files = protocolFiles.map((f) => ({
            ...f,
            title: f.title.trim() || (f.path ? f.path.split('/').pop() : 'קובץ'),
        }));
        return { errors, settings: { protocol, aap2022, protocolFiles: files } };
    };

    const handleSave = async () => {
        if (!selectedHospital || !canEdit) return;
        const { errors, settings } = buildSettings();
        if (errors.length > 0) {
            showNotification(errors[0], 'error');
            return;
        }
        try {
            setSaving(true);
            const response = await API.post(
                `/api/configs/${appKey}/${configName}/${selectedHospital}`,
                { data: settings }
            );
            if (response.data?.success) {
                setHasStoredAapConfig(true);
                // Nothing references these any more, so remove the blobs. Best
                // effort, and deliberately so: the server refuses a path outside
                // this hospital's folder, which is exactly what happens when the
                // removed row was inherited from the shared "apps" default — the
                // file stays, as it must, and this hospital simply stops showing
                // it. A failure here leaves an unreferenced file in storage,
                // which costs nothing the admin can see; losing the save it is
                // attached to would.
                await Promise.all(
                    pendingFileDeletions.map((path) =>
                        deleteConfigFile(appKey, configName, selectedHospital, path)
                            .catch((err) => console.warn('Failed to delete removed config file:', path, err))
                    )
                );
                loadDraft(normalizePhototherapySettings(response.data.data?.data ?? settings, variant));
                showNotification('ההגדרות נשמרו בהצלחה');
            } else {
                showNotification('שגיאה בשמירת ההגדרות', 'error');
            }
        } catch (error) {
            console.error('Error saving phototherapy settings:', error);
            showNotification('שגיאה בשמירת ההגדרות', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Loads the shipped defaults into the form only. Nothing changes for the
    // hospital until "שמור" writes them as its AppConfig row. The uploaded
    // documents are deliberately kept: they are the department's own files, not
    // a clinical default anyone would want reset by this button.
    const handleRestoreDefaults = () => {
        loadDraft({ ...defaults, protocol, protocolFiles });
        setDirty(true);
        showNotification('נטענו ברירות המחדל - יש ללחוץ "שמור" כדי להחילן', 'info');
    };

    const markDirty = () => setDirty(true);

    const updateBand = (index, key, value) => {
        setBands((prev) => prev.map((b, i) => (i === index ? { ...b, [key]: value } : b)));
        markDirty();
    };
    const addBand = () => {
        setBands((prev) => [...prev, { minDelta: '', maxDelta: '', recommendation: '' }]);
        markDirty();
    };
    const removeBand = (index) => {
        setBands((prev) => prev.filter((_, i) => i !== index));
        markDirty();
    };

    const renderTextareaField = (f) => (
        <div className={`ptc-field${f.tab === 'followup' ? ' ptc-field--wide' : ''}`} key={f.key}>
            <label className="ptc-label" htmlFor={`list-${f.key}`}>
                {f.label}
            </label>
            <textarea
                id={`list-${f.key}`}
                className="ptc-input ptc-input--list"
                rows={f.rows}
                value={lists[f.key] ?? ''}
                disabled={!canEdit}
                onChange={(e) => {
                    setLists((prev) => ({ ...prev, [f.key]: e.target.value }));
                    markDirty();
                }}
            />
            <span className="ptc-help">{f.help}</span>
        </div>
    );

    const showHospitalDropdown = user?.isSuperAdmin || hospitalsWithEditAccess.length > 1;
    const appLink = `https://my.mashlom.me/main/${selectedHospital || 'apps'}/${variant.appToken}`;
    const isAap = protocol === 'aap2022';

    return (
        <CommonLayout>
            <div className="phototherapy-config-container">
                <div className="ptc-header">
                    <Typography variant="h4" component="h1" className="header-title">
                        {`ניהול מחשבון ${variant.title}`}
                    </Typography>
                    <a href={appLink} target="_blank" rel="noopener noreferrer" className="app-link">
                        לאפליקציה <ArrowLeftIcon />
                    </a>
                </div>

                {showHospitalDropdown && (
                    <div className="ptc-hospital-selector">
                        <DropdownWithIcon
                            icon={hospitalIcon}
                            placeholder="בית חולים"
                            value={selectedHospital}
                            onChange={(e) => setSelectedHospital(e.target.value)}
                        >
                            {(user?.isSuperAdmin ? availableHospitals : hospitalsWithEditAccess).map((hospital) => (
                                <option key={hospital._id} value={hospital.nameEnglish}>
                                    {hospital.nameHebrew}
                                </option>
                            ))}
                        </DropdownWithIcon>
                    </div>
                )}

                <Loader isLoading={loading} />

                <div className="ptc-description">
                    כאן נקבע איזו גרסה של מחשבון הצהבת מוצגת לבית החולים.
                    המעבר לעקומות AAP 2022 מאפשר גם התאמה של הספים והטקסטים לנהלי בית החולים.
                </div>

                {!canEdit && selectedHospital && (
                    <div className="ptc-readonly">
                        אין לך הרשאת עריכה עבור בית חולים זה - ההגדרות מוצגות לקריאה בלבד.
                    </div>
                )}

                <section className="ptc-card">
                    <h2 className="ptc-card-title">גרסת המחשבון</h2>
                    <div className="ptc-protocol-options" role="radiogroup" aria-label="protocol">
                        {[
                            {
                                value: 'israeliCurves',
                                title: 'עקומות ישראליות',
                                help: 'שתי קבוצות גיל הריון (35-37 / 38+), עקומת בוטאני ואזורי סיכון.',
                            },
                            {
                                value: 'aap2022',
                                title: 'AAP 2022',
                                help: 'עקומות לפי שבוע הריון (35-40+) וגורמי סיכון לנוירוטוקסיות, ספי הסלמת טיפול והחלפת דם, ומעקב לאחר שחרור לפי ההפרש מסף הטיפול. הערכים ניתנים לעריכה בלשוניות שלמטה.',
                            },
                        ].map((opt) => (
                            <label
                                key={opt.value}
                                className={`ptc-protocol-option ${protocol === opt.value ? 'selected' : ''} ${canEdit ? '' : 'disabled'}`}
                            >
                                <input
                                    type="radio"
                                    name="protocol"
                                    value={opt.value}
                                    checked={protocol === opt.value}
                                    disabled={!canEdit}
                                    onChange={() => { setProtocol(opt.value); markDirty(); }}
                                />
                                <span className="ptc-protocol-body">
                                    <span className="ptc-protocol-title">{opt.title}</span>
                                    {/* Which version is the fallback depends on the app, not on the
                                        calculator — see apps/phototherapy/variants.ts. */}
                                    {opt.value === variant.defaultProtocol && (
                                        <span className="ptc-protocol-badge">
                                            ברירת המחדל - מוצגת לכל בית חולים שלא הוגדר אחרת
                                        </span>
                                    )}
                                    <span className="ptc-protocol-help">{opt.help}</span>
                                </span>
                            </label>
                        ))}
                    </div>
                </section>

                {isAap && (
                    <>

                        <div className="ptc-tabs" role="tablist">
                            {TABS.map((tab) => (
                                <button
                                    key={tab.key}
                                    role="tab"
                                    aria-selected={activeTab === tab.key}
                                    className={`ptc-tab ${activeTab === tab.key ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {activeTab === 'thresholds' && (
                            <section className="ptc-card">
                                {NUMBER_GROUPS.map((group) => (
                                    <div className="ptc-group" key={group.title}>
                                        <h3 className="ptc-group-title">{group.title}</h3>
                                        <div className="ptc-number-grid">
                                            {group.fields.map((f) => (
                                                <div className="ptc-field" key={f.key}>
                                                    <label className="ptc-label" htmlFor={`num-${f.key}`}>
                                                        {f.label}
                                                    </label>
                                                    <div className="ptc-input-with-unit">
                                                        <input
                                                            id={`num-${f.key}`}
                                                            className="ptc-input ptc-input--num"
                                                            type="text"
                                                            inputMode="decimal"
                                                            dir="ltr"
                                                            value={numbers[f.key] ?? ''}
                                                            placeholder={f.nullable ? 'ריק' : ''}
                                                            disabled={!canEdit}
                                                            onChange={(e) => {
                                                                setNumbers((prev) => ({ ...prev, [f.key]: e.target.value }));
                                                                markDirty();
                                                            }}
                                                        />
                                                        <span className="ptc-unit">{f.unit}</span>
                                                    </div>
                                                    <span className="ptc-help">{f.help}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </section>
                        )}

                        {activeTab === 'followup' && (
                            <section className="ptc-card">
                                <h2 className="ptc-card-title">מעקב לאחר שחרור - לפי ההפרש מסף טיפול באור</h2>
                                <p className="ptc-section-help">
                                    {`ההפרש = סף טיפול באור לגיל היילוד פחות ערך הבילירובין (${MG_DL}). השורות ממוינות אוטומטית לפי הערך המזערי; ערך מרבי ריק = ללא גבול עליון.`}
                                </p>

                                <div className="ptc-bands">
                                    {bands.map((band, i) => (
                                        <div className="ptc-band" key={i}>
                                            <div className="ptc-band-range">
                                                <span className="ptc-band-range-label">הפרש</span>
                                                <input
                                                    className="ptc-input ptc-input--num"
                                                    type="text"
                                                    inputMode="decimal"
                                                    dir="ltr"
                                                    value={band.minDelta}
                                                    disabled={!canEdit}
                                                    aria-label={`הפרש מזערי, שורה ${i + 1}`}
                                                    onChange={(e) => updateBand(i, 'minDelta', e.target.value)}
                                                />
                                                <span className="ptc-band-dash">–</span>
                                                <input
                                                    className="ptc-input ptc-input--num"
                                                    type="text"
                                                    inputMode="decimal"
                                                    dir="ltr"
                                                    placeholder="∞"
                                                    value={band.maxDelta}
                                                    disabled={!canEdit}
                                                    aria-label={`הפרש מרבי, שורה ${i + 1}`}
                                                    onChange={(e) => updateBand(i, 'maxDelta', e.target.value)}
                                                />
                                                <span className="ptc-unit">{MG_DL}</span>
                                            </div>
                                            <textarea
                                                className="ptc-input ptc-band-text"
                                                rows={2}
                                                value={band.recommendation}
                                                placeholder="ההמלצה שתוצג לצוות"
                                                disabled={!canEdit}
                                                aria-label={`המלצה, שורה ${i + 1}`}
                                                onChange={(e) => updateBand(i, 'recommendation', e.target.value)}
                                            />
                                            {canEdit && (
                                                <button
                                                    type="button"
                                                    className="ptc-band-remove"
                                                    onClick={() => removeBand(i)}
                                                    aria-label={`הסר שורה ${i + 1}`}
                                                    title="הסר שורה"
                                                >
                                                    <FaRegTrashAlt size={16} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {canEdit && (
                                    <Button
                                        variant="outlined"
                                        onClick={addBand}
                                        startIcon={<FaPlus size={13} />}
                                        sx={{
                                            borderColor: '#103C6E',
                                            color: '#103C6E',
                                            borderRadius: '8px',
                                            padding: '8px 20px',
                                            '& .MuiButton-startIcon': { marginLeft: '8px', marginRight: '-2px' },
                                        }}
                                    >
                                        הוסף שורה
                                    </Button>
                                )}

                                <div className="ptc-field ptc-field--wide">
                                    <label className="ptc-label" htmlFor="before-12h">
                                        הנחיה ליילוד מתחת לגיל 12 שעות
                                    </label>
                                    <textarea
                                        id="before-12h"
                                        className="ptc-input"
                                        rows={3}
                                        value={before12hText}
                                        disabled={!canEdit}
                                        onChange={(e) => { setBefore12hText(e.target.value); markDirty(); }}
                                    />
                                    <span className="ptc-help">
                                        מוצגת במקום המלצת המעקב, כי לא ניתן לקבוע מועד מעקב לפי מדידה מוקדמת כל כך.
                                    </span>
                                </div>

                                {FOLLOW_UP_TEXTAREA_FIELDS.map(renderTextareaField)}
                            </section>
                        )}

                        {activeTab === 'texts' && (
                            <section className="ptc-card">
                                <div className="ptc-lists-grid">
                                    {TEXTS_TAB_FIELDS.map(renderTextareaField)}
                                </div>
                            </section>
                        )}

                        {activeTab === 'files' && (
                            <section className="ptc-card">
                                <h2 className="ptc-card-title">פרוטוקולים להורדה</h2>
                                <p className="ptc-section-help">
                                    הקבצים מוצגים במסך "פרוטוקולים" באפליקציה, לפי הסדר שכאן, וכל אחד מוצג
                                    בכותרת שנקבעה לו. ללא קבצים - המסך אינו מופיע כלל.
                                    כל מי שמגיע לאפליקציה יכול לפתוח להוריד את הקבצים - אין להעלות מסמכים שאינם מיועדים לפרסום.
                                    מחיקה והעלאה נכנסות לתוקף רק לאחר לחיצה על "שמור".
                                </p>

                                <ConfigFilesEditor
                                    appName={appKey}
                                    configName={configName}
                                    hospital={selectedHospital}
                                    files={protocolFiles}
                                    disabled={!canEdit}
                                    hrefFor={protocolFileHref}
                                    emptyText='לא הוגדרו קבצים - מסך "פרוטוקולים" לא יופיע באפליקציה.'
                                    onChange={(next) => { setProtocolFiles(next); markDirty(); }}
                                    onRemoveFile={(path) => setPendingFileDeletions((prev) => [...prev, path])}
                                />
                            </section>
                        )}
                    </>
                )}

                {canEdit && (
                    <div className="ptc-actions">
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={saving || !dirty || !selectedHospital}
                            sx={{
                                backgroundColor: '#103C6E',
                                borderRadius: '8px',
                                padding: '10px 32px',
                                '&:hover': { backgroundColor: '#0d2e54' },
                                '&:disabled': { backgroundColor: '#ccc', color: '#fff' },
                            }}
                        >
                            {saving ? 'שומר...' : 'שמור'}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleRestoreDefaults}
                            disabled={saving}
                            sx={{
                                borderColor: '#103C6E',
                                color: '#103C6E',
                                borderRadius: '8px',
                                padding: '10px 24px',
                            }}
                        >
                            שחזר ברירות מחדל
                        </Button>
                        {dirty && <span className="ptc-unsaved">יש שינויים שטרם נשמרו</span>}
                    </div>
                )}
            </div>

            {notification.open && (
                <div className={`ptc-notification ${notification.severity}`} dir="rtl">
                    {notification.message}
                </div>
            )}
        </CommonLayout>
    );
};

export default PhototherapyConfigPage;
